import { FC, useCallback, useEffect, useState } from 'react';

import {
  ArrowsExpandIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  DocumentDownloadIcon,
  EyeIcon,
  MailIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from '@heroicons/react/outline';

import {
  AuditLogActorType,
  ActivityType,
  useAuditLogsLazyQuery,
} from '../../../../generated/graphql';

import { getFullDateTimeStamp, getRelativeTimestamp } from '../../../lib/time';

import Avatar from '../../Avatar';
import ModalDialog from '../../ModalDialog';
import Spinner from '../../../svgs/Spinner';

import homecomingLogo from '../../../../assets/images/logo/logo-green-xl.png';

const PROGRAM_ACTIVITY_ENTITY_TYPE =
  'app.api.models.program_activity.ProgramActivity';
const PAGE_SIZE = 20;

const ACTION_LABEL: Record<string, string> = {
  sent: 'Sent',
  completed: 'Completed',
  provider_signed: 'Signed',
  dismissed: 'Dismissed',
  seen: 'Seen',
  downloaded_pdf: 'Downloaded PDF',
  responses_viewed: 'Viewed responses',
  voided: 'Voided',
  unvoided: 'Restored',
};

const getActionIcon = (activityType: string) => {
  const iconClass = 'h-3.5 w-3.5 shrink-0';
  switch (activityType) {
    case 'sent':
      return <MailIcon className={iconClass} />;
    case 'completed':
      return <CheckCircleIcon className={iconClass} />;
    case 'provider_signed':
      return <PencilIcon className={iconClass} />;
    case 'dismissed':
      return <XIcon className={iconClass} />;
    case 'seen':
      return <EyeIcon className={iconClass} />;
    case 'downloaded_pdf':
      return <DocumentDownloadIcon className={iconClass} />;
    case 'responses_viewed':
      return <ArrowsExpandIcon className={iconClass} />;
    case 'voided':
      return <TrashIcon className={iconClass} />;
    case 'unvoided':
      return <PlusIcon className={iconClass} />;
    default:
      return null;
  }
};

type ExtraMetadata = {
  restored_by?: string;
  performed_by?: string;
  client_name?: string | null;
  activity_type?: string | null;
};

const isFormActivityType = (
  activityType?: string | ActivityType | null,
): boolean =>
  activityType === ActivityType.Assessment || activityType === 'Assessment';

const getDetailsForActivity = (
  auditActivityType: string,
  metadata: ExtraMetadata | null | undefined,
  programActivityType?: ActivityType | null,
): string => {
  const itemType = metadata?.activity_type ?? programActivityType ?? null;
  const isForm = isFormActivityType(itemType);

  switch (auditActivityType) {
    case 'sent':
      return isForm ? 'Form sent to client' : 'Sent to client';
    case 'completed':
      if (metadata?.performed_by === 'client') {
        return isForm ? 'Client submitted the form' : 'Client completed';
      }
      return isForm ? 'Form completed' : 'Completed';
    case 'provider_signed':
      return 'Provider signature added';
    case 'dismissed':
      return isForm ? 'Client dismissed the form' : 'Client dismissed';
    case 'seen':
      return isForm ? 'Client opened the form' : 'Client opened';
    case 'downloaded_pdf':
      return isForm ? 'Form downloaded as PDF' : 'Downloaded as PDF';
    case 'responses_viewed':
      return 'Responses viewed';
    case 'unvoided':
      return metadata?.restored_by === 'homecoming_support'
        ? 'Restored by Homecoming support'
        : isForm
        ? 'Form restored'
        : 'Restored';
    case 'voided':
      return isForm ? 'Sent form voided' : 'Voided';
    default:
      return '—';
  }
};

type Activity = {
  id: string;
  activityType: string;
  actorType?: AuditLogActorType | null;
  performedByProviderUser?: {
    id: string;
    name?: string | null;
    profileImageMedia?: { url: string } | null;
  } | null;
  performedAt: string;
  extraMetadata?: unknown;
};

const isSystemActivity = (activity: Activity): boolean =>
  activity.actorType === AuditLogActorType.System &&
  (activity.extraMetadata as ExtraMetadata | null)?.performed_by !== 'client';

const isClientActivity = (activity: Activity): boolean =>
  (activity.extraMetadata as ExtraMetadata | null)?.performed_by === 'client';

const getTeamMemberDisplay = (activity: Activity) => {
  const metadata = (activity.extraMetadata ?? null) as ExtraMetadata | null;

  if (isSystemActivity(activity)) {
    return {
      name: 'Homecoming',
      subtitle: 'System action',
      imageUrl: undefined,
      isSystem: true,
    };
  }

  if (isClientActivity(activity)) {
    return {
      name: metadata?.client_name ?? 'Client',
      subtitle: 'Client action',
      imageUrl: undefined,
      isSystem: false,
    };
  }

  return {
    name: activity.performedByProviderUser?.name ?? 'Unknown user',
    subtitle: null as string | null,
    imageUrl: activity.performedByProviderUser?.profileImageMedia?.url,
    isSystem: false,
  };
};

type ProgramActivityActivityModalProps = {
  isOpen: boolean;
  setClosed: () => void;
  programActivityId: string;
  activityName?: string | null;
  activityType?: ActivityType | null;
};

const ProgramActivityActivityModal: FC<ProgramActivityActivityModalProps> = ({
  isOpen,
  setClosed,
  programActivityId,
  activityName,
  activityType,
}) => {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allActivities, setAllActivities] = useState<Activity[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fetchAuditLogs, { loading }] = useAuditLogsLazyQuery({
    fetchPolicy: 'network-only',
  });

  const loadPage = useCallback(
    async (pageOffset: number, append: boolean) => {
      setLoadError(null);
      const result = await fetchAuditLogs({
        variables: {
          entityType: PROGRAM_ACTIVITY_ENTITY_TYPE,
          entityId: programActivityId,
          limit: PAGE_SIZE,
          offset: pageOffset,
        },
      });
      if (result.error) {
        setLoadError('Unable to load activity. Please try again.');
        setHasMore(false);
        return 0;
      }
      const next = (result.data?.auditLogs ?? []) as Activity[];
      setAllActivities((prev) => (append ? [...prev, ...next] : next));
      setOffset(pageOffset + next.length);
      setHasMore(next.length >= PAGE_SIZE);
      return next.length;
    },
    [fetchAuditLogs, programActivityId],
  );

  useEffect(() => {
    if (isOpen && programActivityId) {
      setOffset(0);
      setAllActivities([]);
      setHasMore(true);
      setLoadError(null);
      loadPage(0, false);
    }
  }, [isOpen, loadPage, programActivityId]);

  const handleLoadMore = useCallback(() => {
    loadPage(offset, true);
  }, [loadPage, offset]);

  return (
    <ModalDialog
      title={activityName ? `Activity: ${activityName}` : 'Activity'}
      isOpen={isOpen}
      setClosed={setClosed}
      fetching={false}
      width="large"
      overflow="auto"
      hideFooter
      bodyClassName="min-h-[200px]"
      className="sm:min-w-[720px]"
    >
      <div className="flex flex-col">
        {loading && allActivities.length === 0 ? (
          <div className="flex justify-center py-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <>
            {allActivities.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-neutral-75">
                <table className="w-full min-w-[640px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-neutral-75 bg-neutral-25">
                      <th className="whitespace-nowrap px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Team member
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Action
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Details
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allActivities.map((activity) => {
                      const teamMember = getTeamMemberDisplay(activity);
                      const actionLabel =
                        ACTION_LABEL[activity.activityType] ??
                        activity.activityType;
                      const metadata = (activity.extraMetadata ??
                        null) as ExtraMetadata | null;
                      const details = getDetailsForActivity(
                        activity.activityType,
                        metadata,
                        activityType,
                      );

                      return (
                        <tr
                          key={activity.id}
                          className={
                            teamMember.isSystem
                              ? 'border-b border-neutral-50 bg-secondary-20 last:border-b-0'
                              : 'border-b border-neutral-50 last:border-b-0'
                          }
                        >
                          <td className="whitespace-nowrap px-4 py-3">
                            <div className="flex items-center gap-2">
                              {teamMember.isSystem ? (
                                <div
                                  className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-0.5"
                                  title="Homecoming"
                                >
                                  <img
                                    src={homecomingLogo}
                                    alt="Homecoming"
                                    className="h-full w-full object-contain"
                                  />
                                </div>
                              ) : (
                                <Avatar
                                  size="small"
                                  imageUrl={teamMember.imageUrl}
                                  name={teamMember.name}
                                />
                              )}
                              <div className="min-w-0">
                                <span className="block text-caption text-neutral-150">
                                  {teamMember.name}
                                </span>
                                {teamMember.subtitle && (
                                  <span className="block text-small-caption text-secondary-125">
                                    {teamMember.subtitle}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={
                                teamMember.isSystem
                                  ? 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-25 px-2 py-0.5 text-small-label font-medium text-secondary-150'
                                  : 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-neutral-50 px-2 py-0.5 text-small-label font-medium text-neutral-150'
                              }
                            >
                              {getActionIcon(activity.activityType)}
                              {actionLabel}
                            </span>
                          </td>
                          <td className="max-w-[280px] px-4 py-3 text-small-caption text-neutral-125">
                            <span className="min-w-0 break-words line-clamp-2">
                              {details}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-small-caption text-neutral-125">
                            <span
                              title={
                                activity.performedAt
                                  ? getFullDateTimeStamp(activity.performedAt)
                                  : ''
                              }
                            >
                              {activity.performedAt
                                ? getRelativeTimestamp(activity.performedAt)
                                : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {allActivities.length > 0 && hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="flex items-center gap-1 text-caption font-medium text-secondary-100 hover:text-secondary-150 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Spinner className="h-4 w-4" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load more
                      <ChevronDownIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}
            {!loading && loadError && (
              <div className="py-12 text-center text-caption text-red-100">
                {loadError}
              </div>
            )}
            {!loading && !loadError && allActivities.length === 0 && (
              <div className="py-12 text-center text-caption text-neutral-125">
                No activity yet for this item.
              </div>
            )}
          </>
        )}
      </div>
    </ModalDialog>
  );
};

export default ProgramActivityActivityModal;
